import React, { useState, useEffect } from 'react';
import settingDefinitionsAPI from '../services/settingDefinitionsAPI';

const CustomFieldsManager = ({ appId, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    setting_key: '',
    setting_label: '',
    field_type: 'text',
    field_options: [],
    source_type: 'custom_field',
    user_field_name: '',
    static_value: '',
    protocol_claim_name: '',
    is_required: false,
    default_value: '',
    validation_regex: '',
    description: '',
    placeholder: '',
    display_order: 0
  });
  const [optionInput, setOptionInput] = useState({ value: '', label: '', description: '' });

  useEffect(() => {
    loadFields();
  }, [appId]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const data = await settingDefinitionsAPI.getAppDefinitions(appId, true);
      setFields(data || []);
    } catch (err) {
      console.error('Failed to load custom fields:', err);
      onError?.(err.message || 'Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        setting_key: field.setting_key,
        setting_label: field.setting_label,
        field_type: field.field_type,
        field_options: field.field_options || [],
        source_type: field.source_type || 'custom_field',
        user_field_name: field.user_field_name || '',
        static_value: field.static_value || '',
        protocol_claim_name: field.protocol_claim_name || '',
        is_required: field.is_required,
        default_value: field.default_value || '',
        validation_regex: field.validation_regex || '',
        description: field.description || '',
        placeholder: field.placeholder || '',
        display_order: field.display_order || 0
      });
    } else {
      setEditingField(null);
      setFormData({
        setting_key: '',
        setting_label: '',
        field_type: 'text',
        field_options: [],
        source_type: 'custom_field',
        user_field_name: '',
        static_value: '',
        protocol_claim_name: '',
        is_required: false,
        default_value: '',
        validation_regex: '',
        description: '',
        placeholder: '',
        display_order: fields.length
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingField(null);
    setOptionInput({ value: '', label: '', description: '' });
    setModalError('');
  };

  const handleAddOption = () => {
    if (!optionInput.value || !optionInput.label) {
      setModalError('Option value and label are required');
      return;
    }
    setModalError('');

    const newOption = {
      value: optionInput.value,
      label: optionInput.label,
      description: optionInput.description || undefined
    };

    setFormData({
      ...formData,
      field_options: [...formData.field_options, newOption]
    });
    setOptionInput({ value: '', label: '', description: '' });
  };

  const handleRemoveOption = (index) => {
    setFormData({
      ...formData,
      field_options: formData.field_options.filter((_, i) => i !== index)
    });
  };

  const handleSave = async () => {
    try {
      setModalError('');
      
      // Validation
      if (!formData.setting_key || !formData.setting_label) {
        setModalError('Field key and label are required');
        return;
      }

      if (['select', 'multiselect'].includes(formData.field_type) && formData.field_options.length === 0) {
        setModalError('At least one option is required for select/multiselect fields');
        return;
      }

      setLoading(true);

      if (editingField) {
        // Update existing field
        await settingDefinitionsAPI.updateDefinition(appId, editingField.id, formData);
        onSuccess?.('Custom field updated successfully');
      } else {
        // Create new field
        await settingDefinitionsAPI.createDefinition(appId, formData);
        onSuccess?.('Custom field created successfully');
      }

      handleCloseModal();
      loadFields();
    } catch (err) {
      console.error('Failed to save custom field:', err);
      setModalError(err.message || 'Failed to save custom field');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this custom field? User values will remain but won\'t be used.')) {
      return;
    }

    try {
      setLoading(true);
      await settingDefinitionsAPI.deleteDefinition(appId, fieldId);
      onSuccess?.('Custom field deleted successfully');
      loadFields();
    } catch (err) {
      console.error('Failed to delete custom field:', err);
      onError?.(err.message || 'Failed to delete custom field');
    } finally {
      setLoading(false);
    }
  };

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Text',
      select: 'Dropdown',
      multiselect: 'Multi-Select',
      boolean: 'Checkbox',
      number: 'Number',
      textarea: 'Text Area'
    };
    return labels[type] || type;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Fields &amp; Claims</h2>
            <p className="text-gray-600 mt-1">
              Configure custom fields, user data, and static values sent as SAML attributes or OIDC claims
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover"
          >
            + Add Custom Field
          </button>
        </div>

        {loading && !showModal ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-gray-600">Loading custom fields...</p>
          </div>
        ) : fields.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No custom fields</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new custom field.</p>
            <div className="mt-6">
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-hover"
              >
                + Add Custom Field
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map((field) => (
              <div
                key={field.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{field.setting_label}</h3>
                      {field.is_required && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                          Required
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        field.source_type === 'user_field' ? 'bg-blue-100 text-blue-700' :
                        field.source_type === 'static_value' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-primary-light text-primary'
                      }`}>
                        {field.source_type === 'user_field' ? 'User Field' :
                         field.source_type === 'static_value' ? 'Static Value' :
                         'Custom Field'}
                      </span>
                      {field.protocol_claim_name && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Sent as: {field.protocol_claim_name}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Field Key:</span>
                        <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-gray-800">
                          {field.setting_key}
                        </code>
                      </div>
                      <div>
                        {field.source_type === 'user_field' ? (
                          <>
                            <span className="text-gray-500">User Field:</span>
                            <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-gray-800">
                              {field.user_field_name}
                            </code>
                          </>
                        ) : field.source_type === 'static_value' ? (
                          <>
                            <span className="text-gray-500">Value:</span>
                            <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-gray-800">
                              {field.static_value}
                            </code>
                          </>
                        ) : (
                          <>
                            <span className="text-gray-500">Type:</span>
                            <span className="ml-2 font-medium text-gray-800">
                              {getFieldTypeLabel(field.field_type)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {field.description && (
                      <p className="mt-2 text-sm text-gray-600">{field.description}</p>
                    )}

                    {['select', 'multiselect'].includes(field.field_type) && field.field_options && (
                      <div className="mt-2">
                        <span className="text-sm text-gray-500">Options:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {field.field_options.map((opt, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                              title={opt.description}
                            >
                              {opt.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {field.validation_regex && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-500">Pattern:</span>
                        <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-gray-800">
                          {field.validation_regex}
                        </code>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleOpenModal(field)}
                      className="px-3 py-1 text-sm text-primary hover:bg-primary-light rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(field.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Error message inside modal */}
              {modalError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  <div className="flex justify-between items-start">
                    <span>{modalError}</span>
                    <button onClick={() => setModalError('')} className="ml-2 font-bold hover:text-red-900">×</button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Field Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Key * <span className="text-gray-500 font-normal">(used in API/database)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.setting_key}
                    onChange={(e) => setFormData({ ...formData, setting_key: e.target.value })}
                    placeholder="e.g., Role, Department, EmployeeID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={editingField !== null}
                  />
                  {editingField && (
                    <p className="mt-1 text-xs text-gray-500">Field key cannot be changed after creation</p>
                  )}
                </div>

                {/* Field Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Label *
                  </label>
                  <input
                    type="text"
                    value={formData.setting_label}
                    onChange={(e) => setFormData({ ...formData, setting_label: e.target.value })}
                    placeholder="e.g., Veeam Role, Department, Employee ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Source Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Type *
                  </label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="custom_field">Custom Field (set per role)</option>
                    <option value="user_field">User Field (auto-resolved from user data)</option>
                    <option value="static_value">Static Value (fixed string)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.source_type === 'custom_field' && 'Value is set per-role in Default Settings. Sent as SAML attribute or OIDC claim if a claim name is set.'}
                    {formData.source_type === 'user_field' && 'Value is auto-resolved from the user or company record at sign-in.'}
                    {formData.source_type === 'static_value' && 'A fixed string is sent every time.'}
                  </p>
                </div>

                {/* User Field Name (for user_field source) */}
                {formData.source_type === 'user_field' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User Field *
                    </label>
                    <select
                      value={formData.user_field_name}
                      onChange={(e) => setFormData({ ...formData, user_field_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select field...</option>
                      <option value="email">email</option>
                      <option value="firstname">firstname</option>
                      <option value="lastname">lastname</option>
                      <option value="permission_level">permission_level</option>
                      <optgroup label="Company Fields">
                        <option value="company_id">company_id</option>
                        <option value="company_name">company_name</option>
                        <option value="company_domain">company_domain</option>
                        <option value="company_email">company_email</option>
                      </optgroup>
                    </select>
                  </div>
                )}

                {/* Static Value (for static_value source) */}
                {formData.source_type === 'static_value' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Static Value *
                    </label>
                    <input
                      type="text"
                      value={formData.static_value}
                      onChange={(e) => setFormData({ ...formData, static_value: e.target.value })}
                      placeholder="e.g., Support, Agent, true"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                {/* Protocol Claim Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Send As (Claim/Attribute Name)
                    {formData.source_type !== 'custom_field' && ' *'}
                  </label>
                  <input
                    type="text"
                    value={formData.protocol_claim_name}
                    onChange={(e) => setFormData({ ...formData, protocol_claim_name: e.target.value })}
                    placeholder="e.g., email, firstName, Role"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.source_type === 'custom_field'
                      ? 'Leave empty to nest under custom_fields in OIDC tokens (not sent in SAML). Set a name to send as a top-level attribute/claim.'
                      : 'The name used in SAML assertions and OIDC token claims.'}
                  </p>
                </div>

                {/* Field Type (only for custom_field source) */}
                {formData.source_type === 'custom_field' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Type *
                  </label>
                  <select
                    value={formData.field_type}
                    onChange={(e) => setFormData({ ...formData, field_type: e.target.value, field_options: [] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="text">Text</option>
                    <option value="select">Dropdown (Single Select)</option>
                    <option value="multiselect">Multi-Select</option>
                    <option value="boolean">Checkbox (Yes/No)</option>
                    <option value="number">Number</option>
                    <option value="textarea">Text Area</option>
                  </select>
                </div>
                )}

                {/* Options for select/multiselect (custom_field only) */}
                {formData.source_type === 'custom_field' && ['select', 'multiselect'].includes(formData.field_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options *
                    </label>
                    
                    {/* Add Option Form */}
                    <div className="bg-gray-50 p-3 rounded-lg mb-3">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input
                          type="text"
                          value={optionInput.value}
                          onChange={(e) => setOptionInput({ ...optionInput, value: e.target.value })}
                          placeholder="Value (e.g., admin)"
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          value={optionInput.label}
                          onChange={(e) => setOptionInput({ ...optionInput, label: e.target.value })}
                          placeholder="Label (e.g., Administrator)"
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          value={optionInput.description}
                          onChange={(e) => setOptionInput({ ...optionInput, description: e.target.value })}
                          placeholder="Description (optional)"
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <button
                        onClick={handleAddOption}
                        className="w-full px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-hover"
                      >
                        + Add Option
                      </button>
                    </div>

                    {/* Options List */}
                    {formData.field_options.length > 0 && (
                      <div className="space-y-2">
                        {formData.field_options.map((opt, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{opt.label}</div>
                              <div className="text-xs text-gray-500">Value: {opt.value}</div>
                              {opt.description && (
                                <div className="text-xs text-gray-600 mt-1">{opt.description}</div>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveOption(idx)}
                              className="ml-2 text-red-600 hover:text-red-800"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Help text shown to admins when setting this field"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Placeholder (for custom_field text fields) */}
                {formData.source_type === 'custom_field' && ['text', 'textarea', 'number'].includes(formData.field_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={formData.placeholder}
                      onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                      placeholder="e.g., Enter employee ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                {/* Validation Regex (for custom_field text fields) */}
                {formData.source_type === 'custom_field' && formData.field_type === 'text' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validation Pattern (Regex)
                    </label>
                    <input
                      type="text"
                      value={formData.validation_regex}
                      onChange={(e) => setFormData({ ...formData, validation_regex: e.target.value })}
                      placeholder="e.g., ^EMP-[0-9]{5}$"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                {/* Required Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_required"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="is_required" className="ml-2 block text-sm text-gray-700">
                    Required field (users must provide a value)
                  </label>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingField ? 'Update Field' : 'Create Field'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsManager;
